/**
 * 地圖相關的計算工具函數
 */

// ==================== 類型定義 ====================

/** 經緯度座標 */
export interface LatLng {
  lat: number
  lng: number
}

/** Google Maps LatLng 相容物件 */
export interface GoogleLatLng {
  lat: number | (() => number)
  lng: number | (() => number)
}

/** 地圖操作日誌項目 */
export interface MapLogEntry {
  timestamp: string
  type: string
  prefix: string
  params: unknown
}

/** 地圖操作統計 */
export interface MapStatsData {
  zoom: number
  center: number
  panTo: number
  options: number
  mapType: number
  init: number
  total: number
  [key: string]: number
}

/** 地圖動畫參數 */
export interface MapAnimationParams {
  prefix?: string
  log?: boolean
  [key: string]: unknown
}

/** 動畫結束參數 */
export interface AnimationEndParams {
  zoom: number
  center: GoogleLatLng
  callback?: () => void
}

/** 地圖初始化選項 */
export interface MapInitOptions {
  center?: GoogleLatLng
  zoom?: number
  [key: string]: unknown
}

/** 地圖實例介面（簡化） */
export interface GoogleMapInstance {
  setZoom: (zoom: number) => void
  setCenter: (center: GoogleLatLng) => void
  panTo: (center: GoogleLatLng) => void
  setOptions: (options: MapInitOptions) => void
  setMapTypeId: (mapTypeId: string) => void
  getCenter: () => GoogleLatLng | null
}

/** 地圖調試資訊 */
export interface MapDebugInfo {
  stats: MapStatsData
  logs: MapLogEntry[]
}

/** 格式化的日誌條目（用於 console.table） */
export interface FormattedLogEntry {
  時間: string
  操作: string
  描述: string
  參數: string
}

// ==================== 地圖計算工具 ====================

/**
 * 計算兩點之間的球面距離（米）
 */
export function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000 // 地球半徑（米）
  const dLat = (lat2 - lat1) * Math.PI / 180 // 緯度差（弧度）
  const dLng = (lng2 - lng1) * Math.PI / 180 // 經度差（弧度）
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

/**
 * 根據縮放級別計算偏移量
 */
export function calcOffset(zoom: number): number {
  // 確保縮放級別在有效範圍內
  const clampedZoom = Math.max(1, Math.min(22, zoom))

  // 特殊情況處理
  if (clampedZoom === 1) return -3
  if (clampedZoom === 8) return -1.5
  if (clampedZoom === 17) return -0.002

  if (clampedZoom < 8) {
    // 在 1-8 之間進行線性插值
    const t = (clampedZoom - 1) / (8 - 1)
    return -3 + (-1.5 - (-3)) * t
  }
  else if (clampedZoom < 17) {
    // 在 8-17 之間進行線性插值
    const t = (clampedZoom - 8) / (17 - 8)
    return -1.5 + (-0.002 - (-1.5)) * t
  }
  else {
    // 17-22級的線性插值
    const t = (clampedZoom - 17) / (22 - 17)
    return -0.002 + (-0.0005 - (-0.002)) * t
  }
}

/**
 * 根據距離和縮放差異計算合適的動畫時長
 */
export function calculateAnimationDuration(distance: number): number {
  if (distance > 100000) return 2000      // 100km以上
  else if (distance > 50000) return 1800  // 50km-100km
  else if (distance > 20000) return 1500  // 20km-50km
  else if (distance > 10000) return 1200  // 10km-20km
  return 1000 // 默認1秒
}

/**
 * 根據距離和縮放差異計算合適的中間縮放級別
 */
export function calculateIntermediateZoom(distance: number, zoomDiff: number): number | null {
  if (distance <= 10000 || zoomDiff <= 3) {
    return null
  }

  if (distance > 100000) return 8       // 100km以上
  else if (distance > 50000) return 9   // 50km-100km
  else if (distance > 20000) return 10  // 20km-50km
  else if (distance > 10000) return 11  // 10km-20km

  return null
}

/**
 * 緩動函數：實現平滑的加速和減速效果
 */
export function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
}

/**
 * 計算球面線性插值 (Slerp)，計算兩點之間的中間點
 */
export function calculateIntermediatePoint(
  fromLat: number,
  fromLng: number,
  toLat: number,
  toLng: number,
  progress: number
): LatLng {
  // 將經緯度轉換為弧度
  const phi1 = fromLat * Math.PI / 180
  const lambda1 = fromLng * Math.PI / 180
  const phi2 = toLat * Math.PI / 180
  const lambda2 = toLng * Math.PI / 180

  // 計算兩點之間的角距離
  const d = 2 * Math.asin(
    Math.sqrt(
      Math.pow(Math.sin((phi2 - phi1) / 2), 2) +
      Math.cos(phi1) * Math.cos(phi2) *
      Math.pow(Math.sin((lambda2 - lambda1) / 2), 2)
    )
  )

  // 如果距離為0，直接使用起點
  if (d === 0) {
    return { lat: fromLat, lng: fromLng }
  }

  // 計算插值係數
  const A = Math.sin((1 - progress) * d) / Math.sin(d)
  const B = Math.sin(progress * d) / Math.sin(d)

  // 計算中間點
  const x = A * Math.cos(phi1) * Math.cos(lambda1) + B * Math.cos(phi2) * Math.cos(lambda2)
  const y = A * Math.cos(phi1) * Math.sin(lambda1) + B * Math.cos(phi2) * Math.sin(lambda2)
  const z = A * Math.sin(phi1) + B * Math.sin(phi2)

  // 將三維坐標轉換回經緯度
  const phi3 = Math.atan2(z, Math.sqrt(x * x + y * y))
  const lambda3 = Math.atan2(y, x)

  return {
    lat: phi3 * 180 / Math.PI,
    lng: lambda3 * 180 / Math.PI
  }
}

/**
 * 獲取標記圖標的 URL
 */
export function getIconUrl(color: string): string {
  return `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-${color}.png`
}

// ==================== 地圖操作統計 ====================

/**
 * 地圖操作統計數據，存儲在模塊中全局管理
 */
const mapStats: MapStatsData = {
  zoom: 0,
  center: 0,
  panTo: 0,
  options: 0,
  mapType: 0,
  init: 0,
  total: 0
}

/**
 * 地圖操作日誌，存儲最近的操作記錄
 */
const mapLogs: MapLogEntry[] = []
const MAX_LOGS = 300 // 最多保存300條日誌

/**
 * 獲取地圖操作統計數據
 */
export function getMapStats(): MapStatsData {
  return { ...mapStats }
}

/**
 * 重置地圖操作統計數據
 */
export function resetMapStats(): void {
  Object.keys(mapStats).forEach(key => {
    mapStats[key] = 0
  })
}

/**
 * 獲取地圖操作日誌
 */
export function getMapLogs(limit: number = MAX_LOGS): MapLogEntry[] {
  return mapLogs.slice(0, Math.min(limit, mapLogs.length))
}

/**
 * 清除地圖操作日誌
 */
export function clearMapLogs(): void {
  mapLogs.length = 0
}

/**
 * 將地圖操作日誌轉換為適合 console.table 顯示的格式
 */
export function formatMapLogsForTable(limit: number = 100): FormattedLogEntry[] {
  if (mapLogs.length === 0) {
    return []
  }

  // 格式化日誌條目為適合 console.table 顯示的格式
  return mapLogs.slice(0, limit).map(log => {
    // 為對象類型參數進行轉換
    let paramsStr: string
    if (typeof log.params === 'object') {
      try {
        paramsStr = JSON.stringify(log.params).substring(0, 50)
        if (paramsStr.length === 50) paramsStr += '...'
      } catch (e) {
        paramsStr = '[複雜對象]'
      }
    } else {
      paramsStr = String(log.params)
    }

    return {
      時間: log.timestamp,
      操作: log.type,
      描述: log.prefix || '地圖操作',
      參數: paramsStr
    }
  })
}

/**
 * 顯示地圖操作調試信息，包括統計和最近的日誌
 */
export function showMapDebugInfo(logLimit: number = 300): MapDebugInfo {
  // 獲取統計數據
  const stats = getMapStats()

  // 構建統計表格
  console.group('🗺️ 地圖操作統計')
  console.table(stats)
  console.groupEnd()

  // 顯示最近的日誌
  console.group('🗺️ 最近地圖操作日誌')
  const logsForTable = formatMapLogsForTable(logLimit)
  if (logsForTable.length > 0) {
    console.table(logsForTable)
  } else {
    console.log('無操作日誌')
  }
  console.groupEnd()

  // 獲取日誌副本後清除日誌
  const logs = getMapLogs(logLimit)
  clearMapLogs()

  return {
    stats,
    logs
  }
}

/**
 * 記錄地圖操作日誌（內部函數）
 */
function logMapOperation(type: string, prefix: string, params: unknown, timestamp: string): void {
  // 創建新的日誌條目
  const logEntry: MapLogEntry = {
    timestamp,
    type,
    prefix: prefix || '地圖操作',
    params
  }

  // 添加到日誌數組頭部（最新的在前面）
  mapLogs.unshift(logEntry)

  // 限制日誌數量
  if (mapLogs.length > MAX_LOGS) {
    mapLogs.pop()
  }
}

/**
 * 記錄地圖動畫操作
 */
export function logMapAnimation(
  from: LatLng,
  to: LatLng,
  options: MapAnimationParams = {}
): void {
  const timestamp = new Date().toLocaleTimeString()
  const prefix = options.prefix || '地圖動畫'

  const params = {
    起始位置: from,
    目標位置: to,
    ...options
  }

  // 使用現有的日誌記錄函數
  logMapOperation('animation', prefix, params, timestamp)

  // 可選：直接在控制台輸出，方便調試
  if (options.log === true) {
    console.log(`[${timestamp}] ${prefix}:`, params)
  }
}

/**
 * 記錄自訂地圖操作日誌
 */
export function logMapEvent(type: string, description: string, data: Record<string, unknown> = {}): void {
  const timestamp = new Date().toLocaleTimeString()
  logMapOperation(type, description, data, timestamp)
}

// ==================== 地圖操作函數 ====================

/**
 * 取得 LatLng 的實際數值
 */
function getLatLngValue(latLng: GoogleLatLng): { lat: number; lng: number } {
  return {
    lat: typeof latLng.lat === 'function' ? latLng.lat() : latLng.lat,
    lng: typeof latLng.lng === 'function' ? latLng.lng() : latLng.lng
  }
}

/**
 * 統一的地圖設置函數，處理不同類型的地圖操作
 */
export function setMapOption(
  map: GoogleMapInstance | null,
  type: string,
  params: unknown,
  logPrefix: string = ''
): GoogleMapInstance | void {
  if (!map && type !== 'init') {
    console.error('地圖未初始化，無法執行操作:', type)
    return
  }

  // 更新統計，如果是已知的操作類型
  if (mapStats[type] !== undefined) {
    mapStats[type]++
    mapStats.total++
  }

  // 記錄操作
  const timestamp = new Date().toLocaleTimeString()

  // 為某些操作優化日誌輸出格式
  let logParams = params
  if (type === 'center' || type === 'panTo') {
    const p = params as GoogleLatLng
    if (p && p.lat && p.lng) {
      logParams = getLatLngValue(p)
    }
  } else if (type === 'options') {
    const p = params as MapInitOptions
    if (p && p.center) {
      logParams = {
        ...p,
        center: getLatLngValue(p.center)
      }
    }
  }

  // 記錄到日誌系統
  logMapOperation(type, logPrefix, logParams, timestamp)

  try {
    switch (type) {
      case 'zoom':
        map!.setZoom(params as number)
        break
      case 'center':
        map!.setCenter(params as GoogleLatLng)
        break
      case 'panTo':
        map!.panTo(params as GoogleLatLng)
        break
      case 'options':
        map!.setOptions(params as MapInitOptions)
        break
      case 'mapType':
        map!.setMapTypeId(params as string)
        break
      case 'init': {
        // 初始化地圖的特殊情況，需要在調用處創建地圖實例
        const mapContainer = document.getElementById('map')
        if (!mapContainer) {
          throw new Error('找不到地圖容器元素')
        }
        const google = (window as unknown as { google: { maps: { Map: new (container: HTMLElement, options: MapInitOptions) => GoogleMapInstance } } }).google
        const mapInstance = new google.maps.Map(mapContainer, params as MapInitOptions)
        return mapInstance // 返回新創建的地圖實例
      }
      case 'animationEnd': {
        // 動畫結束的處理
        const { zoom, center, callback } = params as AnimationEndParams

        // 等待一點時間確保之前的渲染完成
        setTimeout(() => {
          // 一次性設置最終狀態
          map!.setOptions({
            zoom: zoom,
            center: center
          })

          console.log(`[${timestamp}] 動畫結束設置完成:`, {
            zoom,
            center: getLatLngValue(center)
          })

          // 執行回調
          if (callback && typeof callback === 'function') {
            callback()
          }
        }, 10)
        break
      }
      default:
        console.warn('未知的地圖操作類型:', type)
        break
    }
  } catch (error) {
    console.error(`地圖操作 ${type} 失敗:`, error)
  }
}

/**
 * 取得 Google Maps 目前中心點
 */
export function getMapCenter(map: GoogleMapInstance | null): LatLng {
  if (!map || !map.getCenter) return { lat: 0, lng: 0 }
  const center = map.getCenter()
  if (!center) return { lat: 0, lng: 0 }
  return getLatLngValue(center)
}

// ==================== 匯出 ====================

export default {
  calculateDistance,
  calcOffset,
  calculateAnimationDuration,
  calculateIntermediateZoom,
  easeInOutCubic,
  calculateIntermediatePoint,
  getIconUrl,
  getMapStats,
  resetMapStats,
  getMapLogs,
  clearMapLogs,
  formatMapLogsForTable,
  showMapDebugInfo,
  logMapAnimation,
  logMapEvent,
  setMapOption,
  getMapCenter
}
